import './FilterDatePicker.scss';
import 'react-datepicker/dist/react-datepicker.css';

import { addMonths, getMonth, getYear, isValid, parse, subMonths } from 'date-fns';
import React, { useCallback, useEffect, useState } from 'react';
import ReactDatePicker from 'react-datepicker';

import bem from '../../../../../utils/bem';

export type CalendarDate = Date | undefined | null;
type DatePickerArgs = CalendarDate | CalendarDate[];

export type FilterDatePickerProps = Readonly<{
  disabled?: boolean;
  onChange?: (date: CalendarDate | CalendarDate[]) => void;
  placeholderText?: string;
  date?: Date;
  dateRange?: CalendarDate[];
  selectsRange?: boolean;
  onApply?: () => void;
  onClear?: () => void;
  /** Relative mode: the computed range is highlighted but the day grid is not clickable. */
  relativeMode?: boolean;
  relativeRange?: { start: Date; end: Date } | null;
  /** Overrides the collapsed input's text. */
  displayValue?: string;
  /** Rendered above the calendar's month/year header. */
  calendarHeaderContent?: React.ReactNode;
}>;

const DISPLAY_FORMAT = 'MM/dd/yyyy';

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_RANGE_START = CURRENT_YEAR - 10;
const YEAR_RANGE_END = CURRENT_YEAR + 10;
const YEARS = Array.from({ length: YEAR_RANGE_END - YEAR_RANGE_START + 1 }, (_, i) => YEAR_RANGE_START + i);

const [block, element] = bem('filter-date-picker');

export function FilterDatePicker({
  onChange,
  placeholderText,
  date,
  dateRange,
  disabled = false,
  selectsRange,
  onApply,
  onClear,
  relativeMode = false,
  relativeRange,
  displayValue,
  calendarHeaderContent,
}: FilterDatePickerProps): JSX.Element {
  const [startDate, setStartDate] = useState<Date | null>();
  const [endDate, setEndDate] = useState<Date | null>();
  const [myRef, setMyRef] = useState<ReactDatePicker>();

  useEffect(() => {
    if (relativeMode) {
      setStartDate(relativeRange?.start ?? null);
      setEndDate(relativeRange?.end ?? null);
      return;
    }

    if (selectsRange && dateRange) {
      setStartDate(dateRange[0]);
      setEndDate(dateRange[1]);
    }

    if (!selectsRange) setStartDate(date);
  }, [date, dateRange, selectsRange, relativeMode, relativeRange?.start, relativeRange?.end]);

  const closeCalendar = (): void => {
    myRef?.setOpen(false);
  };

  const setDateValues = useCallback((args: DatePickerArgs): CalendarDate | CalendarDate[] => {
    if (!Array.isArray(args)) {
      setStartDate(args);
      return args;
    }

    const [startDateValue, endDateValue] = args;
    setStartDate(startDateValue);
    if (args.length === 2) {
      setEndDate(endDateValue);
    }

    return [startDateValue, endDateValue];
  }, []);

  const handleOnChange = useCallback(
    (args: DatePickerArgs): void => {
      // Belt-and-braces: `filterDate` already disables every day cell in relative mode.
      if (relativeMode) return;

      const data = setDateValues(args);
      if (onChange) {
        onChange(data);
      }
    },
    [setDateValues, onChange, relativeMode]
  );

  const handleChangeRaw = useCallback(
    (event: React.FocusEvent<HTMLInputElement>): void => {
      if (!selectsRange) return;
      const raw = event.target.value ?? '';
      const parts = raw.split(' - ');
      if (parts.length !== 2) return;

      const parsedStart = parse(parts[0], DISPLAY_FORMAT, new Date());
      if (isValid(parsedStart)) setStartDate(parsedStart);

      if (parts[1]) {
        const parsedEnd = parse(parts[1], DISPLAY_FORMAT, new Date());
        if (isValid(parsedEnd)) setEndDate(parsedEnd);
      }
    },
    [selectsRange]
  );

  const handleApply = (): void => {
    closeCalendar();
    if (onApply) onApply();
  };

  const handleClear = (): void => {
    setStartDate(null);
    setEndDate(null);
    closeCalendar();
    if (onClear) onClear();
  };

  const syncSelectedDateToMonth = useCallback(
    (newMonth: number, newYear: number): void => {
      if (selectsRange || !startDate) return;
      const daysInNewMonth = new Date(newYear, newMonth + 1, 0).getDate();
      const day = Math.min(startDate.getDate(), daysInNewMonth);
      const updatedDate = new Date(newYear, newMonth, day);
      handleOnChange(updatedDate);
    },
    [selectsRange, startDate, handleOnChange]
  );

  const renderCustomHeader = ({
    date: headerDate,
    changeYear,
    changeMonth,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
  }: {
    date: Date;
    changeYear: (year: number) => void;
    changeMonth: (month: number) => void;
    decreaseMonth: () => void;
    increaseMonth: () => void;
    prevMonthButtonDisabled: boolean;
    nextMonthButtonDisabled: boolean;
  }): JSX.Element => {
    const handlePrevMonth = (): void => {
      decreaseMonth();
      const target = subMonths(headerDate, 1);
      syncSelectedDateToMonth(getMonth(target), getYear(target));
    };

    const handleNextMonth = (): void => {
      increaseMonth();
      const target = addMonths(headerDate, 1);
      syncSelectedDateToMonth(getMonth(target), getYear(target));
    };

    const handleMonthDropdown = (e: React.ChangeEvent<HTMLSelectElement>): void => {
      const month = MONTHS.indexOf(e.target.value);
      changeMonth(month);
      syncSelectedDateToMonth(month, getYear(headerDate));
    };

    const handleYearDropdown = (e: React.ChangeEvent<HTMLSelectElement>): void => {
      const year = Number(e.target.value);
      changeYear(year);
      syncSelectedDateToMonth(getMonth(headerDate), year);
    };

    return (
      <>
        {calendarHeaderContent ? (
          <div className={element('relative-header')} data-testid="calendar-relative-header">
            {calendarHeaderContent}
          </div>
        ) : null}
        <div className={element('header')} data-testid="calendar-custom-header">
          <button
            type="button"
            className={element('nav-button')}
            onClick={handlePrevMonth}
            disabled={prevMonthButtonDisabled}
            aria-label="Previous month"
            data-testid="calendar-prev-month"
          >
            &#8249;
          </button>
          <select
            className={element('header-select')}
            value={MONTHS[getMonth(headerDate)]}
            onChange={handleMonthDropdown}
            data-testid="calendar-month-dropdown"
            aria-label="Select month"
          >
            {MONTHS.map((month) => (
              <option key={month} value={month}>
                {month}
              </option>
            ))}
          </select>
          <select
            className={element('header-select')}
            value={getYear(headerDate)}
            onChange={handleYearDropdown}
            data-testid="calendar-year-dropdown"
            aria-label="Select year"
          >
            {YEARS.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
          <button
            type="button"
            className={element('nav-button')}
            onClick={handleNextMonth}
            disabled={nextMonthButtonDisabled}
            aria-label="Next month"
            data-testid="calendar-next-month"
          >
            &#8250;
          </button>
        </div>
      </>
    );
  };

  // No `value` here — ReactDatePicker clones this element with its own computed value, so it is
  // passed as `displayValue` on the picker instead.
  const Input: JSX.Element = (
    <input className={element('input')} readOnly placeholder={placeholderText} data-testid="filter-datepicker-input" />
  );

  return (
    <div className={block()}>
      <ReactDatePicker
        data-testid="filter-datepicker-component"
        ref={(r: ReactDatePicker): void => {
          setMyRef(r);
        }}
        selectsRange={selectsRange || relativeMode}
        placeholderText={placeholderText}
        // Checked ahead of the library's own formatting; undefined outside relative mode.
        value={displayValue}
        disabled={disabled}
        onChange={handleOnChange}
        onChangeRaw={selectsRange && !relativeMode ? handleChangeRaw : undefined}
        startDate={selectsRange || relativeMode ? startDate : undefined}
        selected={startDate}
        endDate={selectsRange || relativeMode ? endDate : undefined}
        // Disables every day cell while preserving range highlighting.
        filterDate={relativeMode ? (): boolean => false : undefined}
        showPopperArrow={false}
        shouldCloseOnSelect={false}
        customInput={Input}
        renderCustomHeader={renderCustomHeader}
      >
        <div className={element('footer')}>
          <button
            className={element('footer-button', 'clear')}
            onClick={handleClear}
            disabled={disabled}
            data-testid="calendar-clear-btn"
          >
            Clear
          </button>
          <button
            className={element('footer-button', 'apply')}
            onClick={handleApply}
            disabled={disabled}
            data-testid="calendar-apply-btn"
          >
            Apply
          </button>
        </div>
      </ReactDatePicker>
    </div>
  );
}
